import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { Spinner } from "@nextui-org/react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import PageStaticMetaTags from "@/components/common/PageStaticMetaTags";
import TextStyles from "@/components/common/TextStyles";
import { Col } from "@/components/customComponents";
import { usePaywallViewedTracking } from "@/features/tracking/usePaywallViewedTracking";
import usePurchaseOptions from "@/hooks/usePurchaseOptions";

import DurationCard from "./components/DurationCard";
import HeroSection from "./components/HeroSection";
import PrivacySection from "./components/PrivacySection";
import TestimonialsSection from "./components/TestimonialsSection";
import TrustStrip from "./components/TrustStrip";
import useHandlePlanSelect from "./hooks/useHandlePlanSelect";
import buildDurationPlans from "./utils/buildDurationPlans";
import buildFromPriceHeadline from "./utils/buildFromPriceHeadline";
import getBrandDefaults from "./utils/getBrandDefaults";
import parseSalesArguments from "./utils/parseSalesArguments";

const ERROR_TIMEOUT_MS = 3000;

// TODO(karlbernard): [NX-11136] Replace with CMS keys (subscription:page.landing.*) once registered.
const ERROR_TITLE = "Unable to load subscription options";
const ERROR_MESSAGE = "Please try again later or contact support if the issue persists.";
const RECAP_TITLE = "Ready when you are";
const RECAP_CTA = "See plans ↑";

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

const SubscribeLanding = () => {
  usePaywallViewedTracking();
  const { purchaseOptions, isPurchaseOptionsLoading } = usePurchaseOptions();
  const applicationId = basicAppConfig.applicationId;
  const [searchParams] = useSearchParams();
  const [stateIsTimedOut, setStateIsTimedOut] = useState(false);

  const salesArguments = useMemo(() => parseSalesArguments(searchParams), [searchParams]);
  const brandDefaults = useMemo(() => getBrandDefaults(applicationId), [applicationId]);

  // After ERROR_TIMEOUT_MS of no data, assume the API failed
  useEffect(() => {
    const timer = setTimeout(() => {
      setStateIsTimedOut(true);
    }, ERROR_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  // Derived before the early return to keep hook order stable across renders.
  const durationPlans = useMemo(() => (purchaseOptions ? buildDurationPlans(purchaseOptions) : []), [purchaseOptions]);

  // Prefer a query-string override, otherwise fall back to brand defaults.
  const headline = salesArguments.headline || buildFromPriceHeadline(durationPlans);
  const subtitle = salesArguments.subtitle || brandDefaults.subtitle;
  const perks = salesArguments.benefits.length > 0 ? salesArguments.benefits : brandDefaults.perks;
  const heroImage = salesArguments.heroImage || brandDefaults.heroImage;

  const handlePlanSelect = useHandlePlanSelect({ purchaseOptions });

  if (!purchaseOptions) {
    // Only treat it as a failure once the request has settled; while it is still
    // in flight we keep showing the spinner even past the timeout so a slow
    // response does not briefly flash the error UI.
    if (stateIsTimedOut && !isPurchaseOptionsLoading) {
      return (
        <div className="flex items-center justify-center p-5 min-h-screen">
          <Col gap={3} className="items-center text-center">
            <Text variant={TextVariants.HEADING_4_BOLD} className="text-gray-900 dark:text-white">
              {ERROR_TITLE}
            </Text>
            <Text variant={TextVariants.PARAGRAPH_MEDIUM_REGULAR} className="opacity-60">
              {ERROR_MESSAGE}
            </Text>
          </Col>
        </div>
      );
    }
    return <Spinner className={TextStyles.spinner} />;
  }

  return (
    <>
      <PageStaticMetaTags pageId="subscribePage" />
      <div className="min-h-screen bg-white dark:bg-[#060608]" data-testid="SubscribeLanding">
        <HeroSection title={headline} subtitle={subtitle} perks={perks} heroImage={heroImage} />

        <div className="mx-auto max-w-[480px] px-5 pb-12 pt-6 md:max-w-[1080px] md:px-10 md:pb-20 md:pt-10">
          <div className="flex flex-col gap-3 md:flex-row md:items-stretch md:gap-4">
            {durationPlans.map((plan) => (
              <DurationCard
                key={plan.id}
                id={plan.id}
                planId={plan.planId}
                total={plan.total}
                perDay={plan.perDay}
                save={plan.save}
                currency={plan.currency}
                onSelect={handlePlanSelect}
              />
            ))}
          </div>

          <TrustStrip
            members={brandDefaults.trustMetrics.members}
            characters={brandDefaults.trustMetrics.characters}
            rating={brandDefaults.trustMetrics.rating}
          />

          <PrivacySection items={brandDefaults.privacy} />

          <TestimonialsSection testimonials={brandDefaults.testimonials} />

          <div className="flex items-center justify-between gap-3 pb-2 pt-7 md:flex-col md:gap-4 md:pt-14">
            <Text variant={TextVariants.LABEL_LARGE_BOLD} className="font-semibold text-gray-900 dark:text-white md:text-[22px]">
              {RECAP_TITLE}
            </Text>
            <button
              type="button"
              onClick={scrollToTop}
              className="rounded-full bg-blue-500 px-[22px] py-3 text-sm font-bold text-white shadow-[0_8px_22px_hsl(220_95%_60%/0.4)] transition hover:brightness-110 md:px-9 md:py-4 md:text-base"
            >
              {RECAP_CTA}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscribeLanding;
